package com.essntl.features.itinerary.ui.send_message

import com.essntl.core.settings.domain.repository.ConfigurationRepository
import com.essntl.core.utils.utils.loadingmanager.LoadingManager
import com.essntl.core.utils.viewmodel.*
import com.essntl.features.category.domain.repository.CategoryRepository
import com.essntl.features.client.domain.repository.ClientRepository
import com.essntl.features.itinerary.domain.model.ItineraryServiceModel
import com.essntl.features.itinerary.domain.repository.ItineraryRepository
import com.essntl.features.itinerary.domain.use_case.UpdateItineraryServiceUseCase
import com.essntl.features.itinerary.ui.edit_itinerary.MessageSendType
import com.essntl.features.message.domain.useCase.CheckSendContactUseCase
import com.essntl.features.message.domain.useCase.SendItineraryMessageUseCase
import com.essntl.features.profile.domain.use_case.GetCurrentUserUseCase
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime

class SendMessageViewModel(
    private val categoryRepository: CategoryRepository,
    private val itineraryRepository: ItineraryRepository,
    private val clientRepository: ClientRepository,
    private val configurationRepository: ConfigurationRepository,
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val sendItineraryMessageUseCase: SendItineraryMessageUseCase,
    private val updateItineraryServiceUseCase: UpdateItineraryServiceUseCase,
    private val checkSendContactUseCase: CheckSendContactUseCase,
    private val state: MutableStateFlow<SendMessageState>,
    override val scope: CoroutineScope,
    override val loadingManager: LoadingManager,
) : ViewModel {

    init {
        getAllCategory()

        getCurrentProfile()
        collectSuccessEvent(
            flow = getCurrentUserUseCase.successEvent,
            getState = { state.value.getCurrentProfile },
            setState = { newState -> state.update { it.copy(getCurrentProfile = newState) } },
        )

        // Send Itinerary Service Message Use Case
        collectLoading(
            flow = sendItineraryMessageUseCase.state.map { it.loadingSet },
            setState = { state.update { s -> s.copy(sendMessageLoading = it) } },
        )
    }

    fun onEvent(event: SendMessageEvent) {
        when (event) {
            is SendMessageEvent.GetItinerary -> getItinerary(event.itineraryId)
            is SendMessageEvent.GetItineraryServices -> getItineraryServices(event)
            is SendMessageEvent.GetEmails -> getEmails()
            is SendMessageEvent.SendMessageList -> sendMessageList(event)
            is SendMessageEvent.CheckContact -> checkContact(event)
            is SendMessageEvent.SendToWhatsappGroup -> sendMessageToGroup(event)
            is SendMessageEvent.SendContact -> sendContact(event)
        }
    }

    private fun getEmails() =
        getData(
            getData = { configurationRepository.getEmails() },
            getState = { state.value.getEmails },
            setState = { newState -> state.update { it.copy(getEmails = newState) } },
        )

    private fun getAllCategory() =
        getData(
            getData = { categoryRepository.getAll() },
            getState = { state.value.getAllCategory },
            setState = { state.value = state.value.copy(getAllCategory = it) },
        )

    private fun getCurrentProfile() =
        scope.launch {
            getCurrentUserUseCase.get()
        }

    private fun getItinerary(itineraryId: String) =
        getData(
            getData = { itineraryRepository.get(itineraryId) },
            getState = { state.value.getItinerary },
            setState = { state.value = state.value.copy(getItinerary = it) },
            onSuccess = {
                val ids = it.proposal?.clients
                if (!ids.isNullOrEmpty())
                    getAllClients(ids)
            },
        )

    private fun getItineraryServices(event: SendMessageEvent.GetItineraryServices) {
        if (event.itineraryServices.isEmpty())
            getData(
                getData = { itineraryRepository.getItineraryServicesByIds(listOf(event.itineraryServiceId)) },
                getState = { state.value.getItineraryServices },
                setState = { state.value = state.value.copy(getItineraryServices = it) },
            )
        else {
            state.update {
                it.copy(
                    getItineraryServices = State.success(event.itineraryServices),
                )
            }
        }
    }

    private fun getAllClients(ids: List<String>) =
        getData(
            getData = { clientRepository.getByIds(ids) },
            getState = { state.value.getAllClients },
            setState = { state.value = state.value.copy(getAllClients = it) },
        )

    private fun sendMessageList(event: SendMessageEvent.SendMessageList) {
        if (event.messageList.isEmpty()) return
        if (state.value.sendMessageList.isLoading) return

        state.update {
            it.copy(
                sendMessageList = State.loading(),
            )
        }

        scope.launch {
            loadingManager.emitLoading(true)

            sendItineraryMessageUseCase.send(
                messageList = event.messageList,
                onSuccess = {
                    if (event.type != MessageSendType.SUPPLIER_PO) {
                        state.update {
                            it.copy(
                                sendMessageList = State.success(Unit),
                            )
                        }
                    }
                },
                onFailure = {
                    state.update {
                        it.copy(
                            sendMessageList = State.failure(),
                        )
                    }
                },
            )
        }
            .invokeOnCompletion {
                if (event.type == MessageSendType.SUPPLIER_PO) {
                    scope.launch {
                        event.messageList.firstOrNull()?.let { messageModel ->
                            val services = messageModel.itineraryServiceList.map { itineraryServiceModel ->
                                itineraryServiceModel
                                    .getChangedFields(
                                        ItineraryServiceModel.empty.copy(
                                            id = itineraryServiceModel.id,
                                            status = ItineraryServiceModel.Status.POSent,
                                        ),
                                    )
                                    .copy(
                                        supplier = itineraryServiceModel.supplier,
                                        status = ItineraryServiceModel.Status.POSent,
                                        poSentAt = Clock.System.now().toLocalDateTime(TimeZone.UTC).toString(),
                                    )
                            }

                            updateItineraryServiceUseCase.updateBatch(
                                itineraryServices = services,
                                onSuccess = {
                                    state.update {
                                        it.copy(
                                            sendMessageList = State.success(Unit),
                                        )
                                    }
                                }
                            ).onFailure { e ->
                                loadingManager.emitLoading(false)
                                // Handle failure
                            }
                        }
                    }
                }
            }
    }

    private fun checkContact(event: SendMessageEvent.CheckContact) {
        if (state.value.checkContact.isLoading) return

        state.update {
            it.copy(
                checkContact = State.loading(),
            )
        }

        scope.launch {
            loadingManager.emitLoading(true)
            checkSendContactUseCase.checkContact(
                contactId = event.contactId,
                onSuccess = {
                    state.update {
                        it.copy(
                            checkContact = State.success(Unit),
                        )
                    }
                },
                onFailure = {
                    state.update {
                        it.copy(
                            checkContact = State.success(Unit),
                        )
                    }
                },
            )
        }.invokeOnCompletion {
            scope.launch {
                loadingManager.emitLoading(false)
            }
        }
    }

    private fun sendMessageToGroup(event: SendMessageEvent.SendToWhatsappGroup) {
        if (event.messageList.isEmpty() || event.phoneNumbers.isEmpty()) return
        if (state.value.sendMessageList.isLoading) return

        state.update {
            it.copy(
                sendMessageList = State.loading(),
            )
        }

        scope.launch {
            loadingManager.emitLoading(true)

            sendItineraryMessageUseCase.createGroup(
                groupSubject = event.subject,
                groupPhoneNumbers = event.phoneNumbers,
                messageList = event.messageList,
                onSuccess = {
                    state.update {
                        it.copy(
                            sendMessageList = State.success(Unit),
                        )
                    }
                },
                onFailure = {
                    state.update {
                        it.copy(
                            sendMessageList = State.failure(),
                        )
                    }
                },
            )
        }
            .invokeOnCompletion {
                scope.launch {
                    loadingManager.emitLoading(false)
                }
            }
    }

    private fun sendContact(event: SendMessageEvent.SendContact) {
        if (state.value.checkContact.isLoading) return

        state.update {
            it.copy(
                checkContact = State.loading(),
            )
        }

        scope.launch {
            loadingManager.emitLoading(true)
            checkSendContactUseCase.sendContact(
                to = event.to,
                onSuccess = {
                    state.update {
                        it.copy(
                            checkContact = State.success(Unit),
                        )
                    }
                },
                onFailure = {
                    state.update {
                        it.copy(
                            checkContact = State.failure(),
                        )
                    }
                },
            )
        }.invokeOnCompletion {
            scope.launch {
                loadingManager.emitLoading(false)
            }
        }
    }
}
